import React from 'react';
import { Link } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import { 
  QrCode, 
  Sparkles, 
  TrendingUp, 
  Check, 
  ChevronRight,
  Menu,
  X,
  ArrowRight,
  Utensils,
  Smartphone,
  BarChart3,
  Zap,
  Mail,
  Phone,
  Send,
  MessageSquare,
  Gift,
  Bell,
  Star,
  Shield,
  Clock,
  ShoppingBag,
  ScanLine,
  Stamp,
  Languages
} from 'lucide-react';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const Landing = () => {
  const [mobileMenuOpen, setMobileMenuOpen] = React.useState(false);
  const [contactForm, setContactForm] = React.useState({
    name: '',
    restaurant: '',
    email: '',
    phone: '',
    message: ''
  });
  const [isSubmitting, setIsSubmitting] = React.useState(false);

  const handleContactSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    
    try {
      const response = await fetch(`${API_URL}/api/contact`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(contactForm)
      });
      
      if (response.ok) {
        toast.success('Message envoyé !', {
          description: 'Nous vous répondrons dans les plus brefs délais.'
        });
        setContactForm({ name: '', restaurant: '', email: '', phone: '', message: '' });
      } else {
        toast.error('Erreur', {
          description: 'Une erreur est survenue. Veuillez réessayer.'
        });
      }
    } catch (error) {
      toast.error('Erreur', {
        description: 'Une erreur est survenue. Veuillez réessayer.'
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  const features = [
    {
      icon: <QrCode className="h-6 w-6" />,
      title: "Menu QR Digital",
      description: "Vos clients scannent, votre menu s'affiche instantanément. Design épuré, mobile-first."
    },
    {
      icon: <ShoppingBag className="h-6 w-6" />,
      title: "Commande en Ligne",
      description: "Sur place avec un QR par table ou à emporter : vos clients commandent et paient depuis leur téléphone. 0% commission."
    },
    {
      icon: <ScanLine className="h-6 w-6" />,
      title: "Import IA de Menu",
      description: "Une photo de votre carte papier ou un PDF suffit : l'IA extrait vos plats, prix et catégories automatiquement."
    },
    {
      icon: <Stamp className="h-6 w-6" />,
      title: "Carte de Fidélité",
      description: "Fidélité digitale avec QR et wallet client. Vos clients cumulent des points et reviennent plus souvent."
    },
    {
      icon: <Languages className="h-6 w-6" />,
      title: "Traduction IA",
      description: "Votre menu traduit en anglais en un clic. Idéal pour votre clientèle touristique."
    },
    {
      icon: <Sparkles className="h-6 w-6" />,
      title: "Assistant IA",
      description: "Descriptions générées, upsells suggérés, promotions optimisées automatiquement."
    },
    {
      icon: <BarChart3 className="h-6 w-6" />,
      title: "Analytics Avancés",
      description: "Comportement clients, tendances, temps passé sur le menu. Export CSV/JSON inclus."
    },
    {
      icon: <Star className="h-6 w-6" />,
      title: "Collecte d'Avis",
      description: "Liens Google & Tripadvisor intégrés. Boostez votre e-réputation automatiquement."
    },
    {
      icon: <Gift className="h-6 w-6" />,
      title: "Promotions Intelligentes",
      description: "Créez des offres attractives avec suivi des performances en temps réel."
    }
  ];

  const plans = [
    {
      name: "Formule unique",
      price: "19,90",
      period: "/mois HT",
      description: "Sans engagement",
      billing: "30 jours d'essai gratuit — Résiliable à tout moment",
      badge: "30 jours d'essai gratuit",
      features: [
        "Menu digital illimité & QR personnalisé",
        "Commande en ligne : sur place & à emporter",
        "Paiement en ligne Stripe — 0% commission",
        "Import de menu par IA (photo ou PDF)",
        "Traduction du menu par IA",
        "Carte de fidélité digitale",
        "Assistant IA & promotions intelligentes",
        "Analytics avancés & collecte d'avis",
        "Support prioritaire"
      ],
      highlighted: true
    }
  ];

  return (
    <div className="min-h-screen bg-[#FAFAFA]">
      {/* Navigation */}
      <nav className="fixed top-0 left-0 right-0 z-50 bg-white/80 backdrop-blur-xl border-b border-gray-100/50">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center h-16">
            <Link to="/" className="flex items-center gap-2.5">
              <div className="h-9 w-9 rounded-xl bg-gradient-to-br from-orange-500 to-orange-600 flex items-center justify-center shadow-lg shadow-orange-500/20">
                <QrCode className="h-5 w-5 text-white" />
              </div>
              <span className="font-heading font-bold text-lg text-gray-900 tracking-tight">ZOTMENU</span>
            </Link>
            
            <div className="hidden md:flex items-center gap-8">
              <Link to="/explorer" className="text-sm text-gray-600 hover:text-gray-900 transition-colors font-medium">
                Explorer
              </Link>
              <a href="#features" className="text-sm text-gray-600 hover:text-gray-900 transition-colors font-medium">
                Fonctionnalités
              </a>
              <a href="#pricing" className="text-sm text-gray-600 hover:text-gray-900 transition-colors font-medium">
                Tarifs
              </a>
              <a href="#contact" className="text-sm text-gray-600 hover:text-gray-900 transition-colors font-medium">
                Contact
              </a>
              <Link to="/login" className="text-sm text-gray-600 hover:text-gray-900 transition-colors font-medium">
                Connexion
              </Link>
              <Link to="/register">
                <Button 
                  data-testid="nav-register-btn"
                  className="bg-gray-900 hover:bg-gray-800 text-white h-10 px-5 rounded-full text-sm font-medium shadow-lg shadow-gray-900/10 transition-all hover:shadow-xl"
                >
                  Créer mon menu
                </Button>
              </Link>
            </div>

            <button
              className="md:hidden p-2 rounded-lg hover:bg-gray-100"
              onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
            >
              {mobileMenuOpen ? <X className="h-5 w-5" /> : <Menu className="h-5 w-5" />}
            </button>
          </div>
        </div>

        {mobileMenuOpen && (
          <div className="md:hidden bg-white border-t border-gray-100 py-4 px-4">
            <div className="flex flex-col gap-3">
              <Link to="/explorer" className="py-2 text-gray-700 font-medium" onClick={() => setMobileMenuOpen(false)}>
                Explorer
              </Link>
              <a href="#features" className="py-2 text-gray-700 font-medium" onClick={() => setMobileMenuOpen(false)}>
                Fonctionnalités
              </a>
              <a href="#pricing" className="py-2 text-gray-700 font-medium" onClick={() => setMobileMenuOpen(false)}>
                Tarifs
              </a>
              <a href="#contact" className="py-2 text-gray-700 font-medium" onClick={() => setMobileMenuOpen(false)}>
                Contact
              </a>
              <Link to="/login" className="py-2 text-gray-700 font-medium" onClick={() => setMobileMenuOpen(false)}>
                Connexion
              </Link>
              <Link to="/register" onClick={() => setMobileMenuOpen(false)}>
                <Button className="w-full bg-gray-900 hover:bg-gray-800 text-white rounded-full font-medium">
                  Créer mon menu
                </Button>
              </Link>
            </div>
          </div>
        )}
      </nav>

      {/* Hero Section */}
      <section className="pt-28 pb-20 px-4 sm:px-6 lg:px-8">
        <div className="max-w-6xl mx-auto">
          <div className="grid lg:grid-cols-2 gap-12 lg:gap-16 items-center">
            <div className="space-y-8">
              {/* NOUVEAU badge */}
              <div className="inline-flex items-center gap-2.5">
                <span className="relative flex items-center gap-1.5 bg-orange-500 text-white px-3.5 py-1.5 rounded-full text-xs font-bold uppercase tracking-wider shadow-lg shadow-orange-500/30">
                  <Zap className="h-3.5 w-3.5" />
                  Nouveau
                  <span className="absolute -top-1 -right-1 h-3 w-3 bg-orange-400 rounded-full animate-ping" />
                </span>
                <span className="text-sm text-gray-600 font-medium">La révolution digitale pour votre restaurant</span>
              </div>
              
              <h1 className="font-heading text-4xl sm:text-5xl lg:text-[3.5rem] font-bold text-gray-900 leading-[1.1] tracking-tight">
                Votre menu devient
                <span className="block text-transparent bg-clip-text bg-gradient-to-r from-orange-500 to-orange-600 pb-2">
                  intelligent
                </span>
              </h1>
              
              <p className="text-lg text-gray-600 max-w-lg leading-relaxed">
                Transformez votre carte en expérience digitale. QR code élégant, 
                recommandations IA, analytics précis. Augmentez vos ventes naturellement.
              </p>

              {/* Essai gratuit highlight */}
              <div className="flex items-center gap-3 bg-gradient-to-r from-emerald-50 to-teal-50 border border-emerald-200 rounded-2xl p-4">
                <div className="h-12 w-12 rounded-xl bg-emerald-500 flex items-center justify-center flex-shrink-0 shadow-lg shadow-emerald-500/20">
                  <Gift className="h-6 w-6 text-white" />
                </div>
                <div>
                  <p className="font-bold text-emerald-800 text-base">30 jours d'essai gratuit</p>
                  <p className="text-emerald-600 text-sm">Puis 19,90€ HT/mois • Sans engagement • Résiliez à tout moment</p>
                </div>
              </div>
              
              <div className="flex flex-col sm:flex-row gap-4">
                <Link to="/register">
                  <Button 
                    data-testid="cta-register-hero"
                    className="w-full sm:w-auto bg-gray-900 hover:bg-gray-800 text-white h-13 px-8 rounded-full text-base font-semibold shadow-xl shadow-gray-900/10 transition-all hover:shadow-2xl hover:scale-[1.02]"
                  >
                    Créer mon menu
                    <ArrowRight className="ml-2 h-4 w-4" />
                  </Button>
                </Link>
                <a href="https://zotmenu.fr/m/chicken-croc-33d3fb9b" target="_blank" rel="noopener noreferrer">
                  <Button 
                    variant="outline"
                    data-testid="cta-see-menu"
                    className="w-full sm:w-auto h-13 px-7 rounded-full text-base font-medium border-2 border-orange-200 text-orange-600 bg-orange-50/50 hover:bg-orange-100 hover:border-orange-300 transition-all"
                  >
                    <QrCode className="mr-2 h-4 w-4" />
                    Voir un menu démo
                  </Button>
                </a>
              </div>

              {/* Trust signals */}
              <div className="flex flex-wrap items-center gap-6 pt-2 text-sm text-gray-500">
                <span className="flex items-center gap-1.5">
                  <Shield className="h-4 w-4 text-emerald-500" />
                  Sans engagement
                </span>
                <span className="flex items-center gap-1.5">
                  <Clock className="h-4 w-4 text-orange-500" />
                  Prêt en 5 min
                </span>
                <span className="flex items-center gap-1.5">
                  <Smartphone className="h-4 w-4 text-blue-500" />
                  100% mobile
                </span>
              </div>
            </div>

            {/* Hero Image */}
            <div className="relative lg:pl-8">
              <div className="relative">
                <div className="relative z-10 rounded-3xl overflow-hidden shadow-2xl shadow-gray-900/10">
                  <img 
                    src="https://images.unsplash.com/photo-1517248135467-4c7edcad34c4?w=600&h=700&fit=crop"
                    alt="Restaurant moderne"
                    className="w-full h-auto object-cover"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-black/30 via-transparent to-transparent" />
                </div>
                
                {/* Floating Card - Stats */}
                <div className="absolute -left-4 sm:-left-8 bottom-8 z-20 bg-white rounded-2xl shadow-xl shadow-gray-900/10 p-4 border border-gray-100">
                  <div className="flex items-center gap-3">
                    <div className="h-10 w-10 rounded-xl bg-emerald-100 flex items-center justify-center">
                      <TrendingUp className="h-5 w-5 text-emerald-600" />
                    </div>
                    <div>
                      <p className="text-2xl font-bold text-gray-900">+23%</p>
                      <p className="text-xs text-gray-500">Panier moyen</p>
                    </div>
                  </div>
                </div>

                {/* Floating Card - QR */}
                <div className="absolute -right-2 sm:-right-4 top-8 z-20 bg-white rounded-2xl shadow-xl shadow-gray-900/10 p-3 border border-gray-100">
                  <div className="flex items-center gap-2.5">
                    <div className="h-9 w-9 rounded-lg bg-orange-100 flex items-center justify-center">
                      <QrCode className="h-5 w-5 text-orange-600" />
                    </div>
                    <div>
                      <p className="text-sm font-semibold text-gray-900">Menu scanné</p>
                      <p className="text-xs text-gray-500">Il y a 2 min</p>
                    </div>
                  </div>
                </div>

                <div className="absolute -top-8 -right-8 w-64 h-64 bg-orange-100 rounded-full blur-3xl opacity-50" />
                <div className="absolute -bottom-8 -left-8 w-64 h-64 bg-blue-100 rounded-full blur-3xl opacity-50" />
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Offre highlight banner */}
      <section className="py-6 px-4 bg-gray-900">
        <div className="max-w-4xl mx-auto text-center">
          <p className="text-white text-base sm:text-lg font-medium">
            <span className="inline-flex items-center gap-2 bg-orange-500 text-white text-xs font-bold px-2.5 py-1 rounded-full uppercase tracking-wider mr-3">Essai gratuit</span>
            30 jours d'essai gratuit • 19,90€ HT/mois • Sans engagement
          </p>
        </div>
      </section>

      {/* Features Section */}
      <section id="features" className="py-20 px-4 sm:px-6 lg:px-8">
        <div className="max-w-6xl mx-auto">
          <div className="text-center max-w-2xl mx-auto mb-16">
            <div className="inline-flex items-center gap-1.5 bg-orange-50 text-orange-600 px-3 py-1 rounded-full text-xs font-semibold uppercase tracking-wider mb-4">
              <Sparkles className="h-3.5 w-3.5" />
              Fonctionnalités
            </div>
            <h2 className="font-heading text-3xl sm:text-4xl font-bold text-gray-900 mb-4 tracking-tight">
              Tout ce dont vous avez besoin
            </h2>
            <p className="text-lg text-gray-600">
              Des outils simples et puissants pour moderniser votre restaurant.
            </p>
          </div>

          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5">
            {features.map((feature, index) => (
              <Card 
                key={index}
                className="group p-6 bg-white border border-gray-100 rounded-2xl hover:border-orange-200 hover:shadow-lg hover:shadow-orange-500/5 transition-all duration-300"
              >
                <CardContent className="p-0 space-y-4">
                  <div className="h-12 w-12 rounded-xl bg-gray-50 text-gray-700 flex items-center justify-center group-hover:bg-orange-500 group-hover:text-white transition-all duration-300">
                    {feature.icon}
                  </div>
                  <h3 className="font-heading font-semibold text-lg text-gray-900">
                    {feature.title}
                  </h3>
                  <p className="text-gray-600 text-sm leading-relaxed">
                    {feature.description}
                  </p>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* How it works */}
      <section className="py-20 px-4 sm:px-6 lg:px-8 bg-white">
        <div className="max-w-6xl mx-auto">
          <div className="text-center max-w-2xl mx-auto mb-16">
            <h2 className="font-heading text-3xl sm:text-4xl font-bold text-gray-900 mb-4 tracking-tight">
              Prêt en 5 minutes
            </h2>
            <p className="text-lg text-gray-600">
              Configurez votre menu digital en quelques étapes simples.
            </p>
          </div>

          <div className="grid md:grid-cols-3 gap-8">
            {[
              { step: "01", title: "Créez votre compte", desc: "Inscription rapide. Accédez à votre espace en quelques secondes." },
              { step: "02", title: "Ajoutez vos plats", desc: "Importez ou créez votre menu en quelques clics avec l'IA." },
              { step: "03", title: "Partagez le QR", desc: "Imprimez et affichez. Vos clients scannent, c'est tout." }
            ].map((item, i) => (
              <div key={i} className="text-center">
                <div className="inline-flex items-center justify-center h-14 w-14 rounded-2xl bg-orange-50 text-orange-600 font-heading font-bold text-lg mb-5">
                  {item.step}
                </div>
                <h3 className="font-heading font-semibold text-xl text-gray-900 mb-2">{item.title}</h3>
                <p className="text-gray-600">{item.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Pricing */}
      <section id="pricing" className="py-20 px-4 sm:px-6 lg:px-8">
        <div className="max-w-6xl mx-auto">
          <div className="text-center max-w-2xl mx-auto mb-6">
            <div className="inline-flex items-center gap-1.5 bg-emerald-50 text-emerald-600 px-3 py-1 rounded-full text-xs font-semibold uppercase tracking-wider mb-4">
              <Gift className="h-3.5 w-3.5" />
              30 jours d'essai gratuit
            </div>
            <h2 className="font-heading text-3xl sm:text-4xl font-bold text-gray-900 mb-4 tracking-tight">
              Un seul tarif, tout inclus
            </h2>
            <p className="text-lg text-gray-600">
              Sans engagement, résiliez quand vous voulez.
            </p>
          </div>

          {/* Offre rappel */}
          <div className="text-center mb-12">
            <span className="inline-flex items-center gap-2 bg-emerald-100 text-emerald-800 px-5 py-2.5 rounded-full text-sm font-semibold">
              <Gift className="h-4 w-4" />
              30 jours d'essai gratuit — Sans engagement
            </span>
          </div>

          <div className="max-w-md mx-auto">
            {plans.map((plan, index) => (
              <Card 
                key={index}
                className={`relative p-6 rounded-2xl transition-all duration-300 ${
                  plan.highlighted 
                    ? 'bg-gray-900 text-white border-0 scale-105 shadow-2xl shadow-gray-900/20' 
                    : 'bg-white border border-gray-200 hover:border-orange-200 hover:shadow-lg'
                }`}
              >
                {plan.badge && (
                  <div className="absolute -top-3 left-1/2 -translate-x-1/2 bg-orange-500 text-white text-xs font-semibold px-3 py-1 rounded-full whitespace-nowrap">
                    {plan.badge}
                  </div>
                )}
                <CardContent className="p-0 space-y-6">
                  <div>
                    <h3 className={`font-heading font-semibold text-xl mb-1 ${plan.highlighted ? 'text-white' : 'text-gray-900'}`}>
                      {plan.name}
                    </h3>
                    <p className={`text-sm ${plan.highlighted ? 'text-gray-400' : 'text-gray-500'}`}>
                      {plan.description}
                    </p>
                  </div>
                  
                  <div>
                    <div className="flex items-baseline gap-1">
                      <span className={`text-4xl font-bold ${plan.highlighted ? 'text-white' : 'text-gray-900'}`}>
                        {plan.price}€
                      </span>
                      <span className={`text-sm ${plan.highlighted ? 'text-gray-400' : 'text-gray-500'}`}>{plan.period}</span>
                    </div>
                    <p className={`text-xs mt-1 font-medium ${plan.highlighted ? 'text-emerald-400' : 'text-emerald-600'}`}>
                      {plan.billing}
                    </p>
                  </div>

                  <ul className="space-y-3">
                    {plan.features.map((feature, i) => (
                      <li key={i} className="flex items-start gap-2.5">
                        <Check className={`h-5 w-5 flex-shrink-0 ${plan.highlighted ? 'text-orange-400' : 'text-orange-500'}`} />
                        <span className={`text-sm ${plan.highlighted ? 'text-gray-300' : 'text-gray-600'}`}>
                          {feature}
                        </span>
                      </li>
                    ))}
                  </ul>

                  <Link to="/register" className="block">
                    <Button 
                      data-testid="pricing-cta-button"
                      className={`w-full h-11 rounded-xl font-medium transition-all ${
                        plan.highlighted 
                          ? 'bg-white text-gray-900 hover:bg-gray-100' 
                          : 'bg-gray-900 text-white hover:bg-gray-800'
                      }`}
                    >
                      Commencer l'essai gratuit
                    </Button>
                  </Link>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Contact Section */}
      <section id="contact" className="py-20 px-4 sm:px-6 lg:px-8 bg-white">
        <div className="max-w-6xl mx-auto">
          <div className="grid lg:grid-cols-2 gap-12 items-start">
            <div className="space-y-8">
              <div>
                <h2 className="font-heading text-3xl sm:text-4xl font-bold text-gray-900 mb-4 tracking-tight">
                  Une question ?
                </h2>
                <p className="text-lg text-gray-600 leading-relaxed">
                  Notre équipe est là pour vous accompagner dans la digitalisation de votre restaurant.
                </p>
              </div>
              
              <div className="space-y-6">
                <div className="flex items-start gap-4">
                  <div className="h-12 w-12 rounded-xl bg-orange-100 flex items-center justify-center flex-shrink-0">
                    <Mail className="h-5 w-5 text-orange-600" />
                  </div>
                  <div>
                    <h3 className="font-semibold text-gray-900 mb-1">Email</h3>
                    <p className="text-gray-600">contact@dealrun.re</p>
                  </div>
                </div>
                
                <div className="flex items-start gap-4">
                  <div className="h-12 w-12 rounded-xl bg-orange-100 flex items-center justify-center flex-shrink-0">
                    <Phone className="h-5 w-5 text-orange-600" />
                  </div>
                  <div>
                    <h3 className="font-semibold text-gray-900 mb-1">Téléphone</h3>
                    <p className="text-gray-600">06 93 46 36 28</p>
                  </div>
                </div>
                
                <div className="flex items-start gap-4">
                  <div className="h-12 w-12 rounded-xl bg-orange-100 flex items-center justify-center flex-shrink-0">
                    <MessageSquare className="h-5 w-5 text-orange-600" />
                  </div>
                  <div>
                    <h3 className="font-semibold text-gray-900 mb-1">Réponse rapide</h3>
                    <p className="text-gray-600">Nous répondons sous 24h</p>
                  </div>
                </div>
              </div>
            </div>

            <Card className="p-6 sm:p-8 bg-white border border-gray-100 rounded-2xl shadow-lg">
              <CardContent className="p-0">
                <form onSubmit={handleContactSubmit} className="space-y-5">
                  <div className="grid sm:grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label htmlFor="contact-name" className="text-sm font-medium text-gray-700">
                        Nom *
                      </Label>
                      <Input
                        id="contact-name"
                        type="text"
                        required
                        value={contactForm.name}
                        onChange={(e) => setContactForm(prev => ({ ...prev, name: e.target.value }))}
                        placeholder="Votre nom"
                        className="h-11 rounded-xl border-gray-200 focus:border-orange-500 focus:ring-orange-500"
                        data-testid="contact-name"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="contact-restaurant" className="text-sm font-medium text-gray-700">
                        Restaurant
                      </Label>
                      <Input
                        id="contact-restaurant"
                        type="text"
                        value={contactForm.restaurant}
                        onChange={(e) => setContactForm(prev => ({ ...prev, restaurant: e.target.value }))}
                        placeholder="Nom de votre restaurant"
                        className="h-11 rounded-xl border-gray-200 focus:border-orange-500 focus:ring-orange-500"
                        data-testid="contact-restaurant"
                      />
                    </div>
                  </div>
                  <div className="grid sm:grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label htmlFor="contact-email" className="text-sm font-medium text-gray-700">
                        Email *
                      </Label>
                      <Input
                        id="contact-email"
                        type="email"
                        required
                        value={contactForm.email}
                        onChange={(e) => setContactForm(prev => ({ ...prev, email: e.target.value }))}
                        placeholder="votre@email.com"
                        className="h-11 rounded-xl border-gray-200 focus:border-orange-500 focus:ring-orange-500"
                        data-testid="contact-email"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="contact-phone" className="text-sm font-medium text-gray-700">
                        Téléphone *
                      </Label>
                      <Input
                        id="contact-phone"
                        type="tel"
                        required
                        value={contactForm.phone}
                        onChange={(e) => setContactForm(prev => ({ ...prev, phone: e.target.value }))}
                        placeholder="06 12 34 56 78"
                        className="h-11 rounded-xl border-gray-200 focus:border-orange-500 focus:ring-orange-500"
                        data-testid="contact-phone"
                      />
                    </div>
                  </div>
                  
                  <div className="space-y-2">
                    <Label htmlFor="contact-message" className="text-sm font-medium text-gray-700">
                      Message *
                    </Label>
                    <Textarea
                      id="contact-message"
                      required
                      value={contactForm.message}
                      onChange={(e) => setContactForm(prev => ({ ...prev, message: e.target.value }))}
                      placeholder="Comment pouvons-nous vous aider ?"
                      rows={4}
                      className="rounded-xl border-gray-200 focus:border-orange-500 focus:ring-orange-500 resize-none"
                      data-testid="contact-message"
                    />
                  </div>
                  
                  <Button 
                    type="submit"
                    disabled={isSubmitting}
                    className="w-full h-12 bg-gray-900 hover:bg-gray-800 text-white rounded-xl font-medium transition-all"
                    data-testid="contact-submit"
                  >
                    {isSubmitting ? 'Envoi en cours...' : (
                      <>
                        Envoyer le message
                        <Send className="ml-2 h-4 w-4" />
                      </>
                    )}
                  </Button>
                </form>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 px-4 sm:px-6 lg:px-8">
        <div className="max-w-4xl mx-auto">
          <div className="relative bg-gradient-to-br from-gray-900 to-gray-800 rounded-3xl p-10 sm:p-16 text-center overflow-hidden">
            <div className="absolute inset-0 opacity-10">
              <div className="absolute top-0 left-0 w-40 h-40 bg-orange-500 rounded-full blur-3xl" />
              <div className="absolute bottom-0 right-0 w-40 h-40 bg-blue-500 rounded-full blur-3xl" />
            </div>
            
            <div className="relative z-10">
              <div className="inline-flex items-center gap-1.5 bg-orange-500/20 text-orange-400 px-3 py-1 rounded-full text-xs font-semibold uppercase tracking-wider mb-6">
                <Zap className="h-3.5 w-3.5" />
                Essai gratuit
              </div>
              <h2 className="font-heading text-3xl sm:text-4xl font-bold text-white mb-4">
                Prêt à transformer votre restaurant ?
              </h2>
              <p className="text-lg text-gray-400 mb-3 max-w-xl mx-auto">
                Profitez de 30 jours d'essai gratuit et découvrez la puissance de ZOTMENU.
              </p>
              <p className="text-sm text-gray-500 mb-8">
                Sans engagement • Configuration en 5 minutes
              </p>
              <Link to="/register">
                <Button 
                  data-testid="cta-register-bottom"
                  className="bg-white text-gray-900 hover:bg-gray-100 h-12 px-8 rounded-full text-base font-semibold shadow-xl transition-all hover:scale-105"
                >
                  Créer mon menu
                  <ArrowRight className="ml-2 h-4 w-4" />
                </Button>
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-white border-t border-gray-100 py-12 px-4 sm:px-6 lg:px-8">
        <div className="max-w-6xl mx-auto">
          <div className="grid md:grid-cols-4 gap-8 mb-10">
            <div className="md:col-span-2">
              <Link to="/" className="flex items-center gap-2.5 mb-4">
                <div className="h-9 w-9 rounded-xl bg-gradient-to-br from-orange-500 to-orange-600 flex items-center justify-center">
                  <QrCode className="h-5 w-5 text-white" />
                </div>
                <span className="font-heading font-bold text-lg text-gray-900">ZOTMENU</span>
              </Link>
              <p className="text-gray-600 text-sm max-w-xs leading-relaxed">
                La solution digitale pour transformer votre menu en machine à vendre.
              </p>
            </div>
            
            <div>
              <h4 className="font-semibold text-gray-900 mb-4">Navigation</h4>
              <ul className="space-y-2.5">
                <li><a href="#features" className="text-sm text-gray-600 hover:text-gray-900 transition-colors">Fonctionnalités</a></li>
                <li><a href="#pricing" className="text-sm text-gray-600 hover:text-gray-900 transition-colors">Tarifs</a></li>
                <li><a href="#contact" className="text-sm text-gray-600 hover:text-gray-900 transition-colors">Contact</a></li>
              </ul>
            </div>
            
            <div>
              <h4 className="font-semibold text-gray-900 mb-4">Légal</h4>
              <ul className="space-y-2.5">
                <li><Link to="/mentions-legales" className="text-sm text-gray-600 hover:text-gray-900 transition-colors">Mentions légales</Link></li>
                <li><Link to="/cgu" className="text-sm text-gray-600 hover:text-gray-900 transition-colors">CGU</Link></li>
                <li><Link to="/confidentialite" className="text-sm text-gray-600 hover:text-gray-900 transition-colors">Confidentialité</Link></li>
              </ul>
            </div>
          </div>
          
          <div className="pt-8 border-t border-gray-100 flex flex-col sm:flex-row justify-between items-center gap-4">
            <p className="text-gray-500 text-sm">
              © 2025 ZOTMENU - SARL DEALRUN. Tous droits réservés.
            </p>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default Landing;
